import {
  cursorPaging,
  fetchAllPages,
  type AccessFilter,
  type PortalAccessRow,
  type PortalBulkInviteInput,
  type PortalChangeRequest,
  type PortalPaymentAttention,
  type PortalRequestItem,
  type PortalViewAsSession,
  type PortalSettingsPatch,
} from "@clubcore/api";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useScope } from "../members/scope";

export function useAccessList(filter: AccessFilter) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "portal-access", filter],
    ...cursorPaging<PortalAccessRow>(({ cursor, signal }) => api.portal.listAccess({ cursor, signal, limit: 50, ...filter })),
  });
}

export function useChangeRequests(status: string) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "portal-requests", status],
    ...cursorPaging<PortalChangeRequest>(({ cursor, signal }) => api.portal.listChangeRequests({ cursor, signal, limit: 50, status })),
  });
}

export function usePortalSettings() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "portal-settings"], queryFn: ({ signal }) => api.portal.readSettings(signal) });
}

/** Mutations refresh the access list (statuses change) and the request queue. */
function useRefresh() {
  const { queryClient, slug } = useScope();
  return () => Promise.all([queryClient.invalidateQueries({ queryKey: [slug, "portal-access"] }), queryClient.invalidateQueries({ queryKey: [slug, "portal-requests"] })]);
}

export function useInvite() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ memberId, email, sendEmail }: { memberId: string; email: string | undefined; sendEmail: boolean }) =>
      api.portal.invite(memberId, { ...(email ? { email } : {}), send_email: sendEmail }),
    onSuccess: refresh,
  });
}

/** The invitation email this member would get, before anything is sent. */
export function useInvitePreview(memberId: string, email: string, enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "portal-invite-preview", memberId, email],
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => api.portal.previewInvite(memberId, email || undefined, signal),
  });
}

export function useInviteMany() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({ mutationFn: (input: PortalBulkInviteInput) => api.portal.inviteMany(input), onSuccess: (_result, input) => (input.dry_run ? undefined : refresh()) });
}

export function useRevoke() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({ mutationFn: ({ memberId, reason }: { memberId: string; reason: string }) => api.portal.revoke(memberId, reason ? { reason } : {}), onSuccess: refresh });
}

export function useReset() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ memberId, kind, sendEmail }: { memberId: string; kind: "pin" | "password"; sendEmail: boolean }) =>
      kind === "pin" ? api.portal.resetPin(memberId, { send_email: sendEmail }) : api.portal.resetPassword(memberId, { send_email: sendEmail }),
    onSuccess: refresh,
  });
}

export function useResetTwoFactor() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({ mutationFn: (memberId: string) => api.portal.resetTwoFactor(memberId), onSuccess: refresh });
}

export function useDecideChange() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, approve, note }: { id: string; approve: boolean; note: string }) =>
      approve ? api.portal.approveChange(id, note ? { note } : {}) : api.portal.rejectChange(id, note ? { note } : {}),
    onSuccess: refresh,
  });
}

export function useUpdatePortalSettings() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (patch: PortalSettingsPatch) => api.portal.updateSettings(patch),
    onSuccess: (settings) => queryClient.setQueryData([slug, "portal-settings"], settings),
  });
}

export function usePaymentsNeedingAttention(options: { enabled?: boolean } = {}) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: [slug, "portal-payments-attention"],
    ...cursorPaging<PortalPaymentAttention>(({ cursor, signal }) => api.portal.paymentsNeedingAttention({ cursor, signal, limit: 50 })),
  });
}

export function useRetryPayment() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (id: string) => api.portal.retryPayment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "portal-payments-attention"] }),
  });
}

export function useResolvePayment() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.portal.resolvePayment(id, { note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "portal-payments-attention"] }),
  });
}

export function useReleaseProtectedField() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ memberId, field }: { memberId: string; field: "email" | "phone" | "address" }) => api.portal.releaseProtectedField(memberId, field),
    onSuccess: refresh,
  });
}

/** Staff "view as member": the club's list of them (needs `audit.read`). */
export function useViewAsSessions(filter: { active?: boolean | undefined; memberId?: string | undefined }) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "portal-view-as", filter],
    ...cursorPaging<PortalViewAsSession>(({ cursor, signal }) => api.portal.listViewAs({ cursor, signal, limit: 50, ...filter })),
  });
}

export function useStartViewAs() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: ({ memberId, reason }: { memberId: string; reason: string }) => api.portal.startViewAs(memberId, { reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "portal-view-as"] }),
  });
}

export function useEndViewAs() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (id: string) => api.portal.endViewAs(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "portal-view-as"] }),
  });
}

// ------------------------------------------------------------------------------------------------ member requests (the inbox)

export interface MemberRequestFilter {
  kind?: "maintenance" | "message" | undefined;
  status: string;
}

export function useMemberRequests(filter: MemberRequestFilter) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "member-requests", filter],
    ...cursorPaging<PortalRequestItem>(({ cursor, signal }) =>
      api.portal.listRequests({ cursor, signal, limit: 50, status: filter.status, ...(filter.kind ? { kind: filter.kind } : {}) }),
    ),
  });
}

/** What waits for staff, for the sidebar. Asked again every minute: nothing pushes it. */
export function useMemberRequestCounts() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "member-request-counts"], queryFn: ({ signal }) => api.portal.requestCounts(signal), refetchInterval: 60_000, staleTime: 30_000, retry: false });
}

export function useMemberAppEditor() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "member-app"], queryFn: ({ signal }) => api.portal.readMemberApp(signal) });
}

export function useRefreshMemberApp() {
  const { queryClient, slug } = useScope();
  return () => queryClient.invalidateQueries({ queryKey: [slug, "member-app"] });
}

export function useMemberRequest(id: string) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "member-request", id], queryFn: ({ signal }) => api.portal.getRequest(id, signal) });
}

function useRefreshMemberRequests() {
  const { queryClient, slug } = useScope();
  return () => Promise.all([
    queryClient.invalidateQueries({ queryKey: [slug, "member-requests"] }),
    queryClient.invalidateQueries({ queryKey: [slug, "member-request"] }),
    queryClient.invalidateQueries({ queryKey: [slug, "member-request-counts"] }),
  ]);
}

export function useReplyToMemberRequest() {
  const { api } = useScope();
  const refresh = useRefreshMemberRequests();
  return useMutation({ mutationFn: ({ id, body }: { id: string; body: string }) => api.portal.replyToRequest(id, { body }), onSuccess: refresh });
}

export function useCloseMemberRequest() {
  const { api } = useScope();
  const refresh = useRefreshMemberRequests();
  return useMutation({ mutationFn: (id: string) => api.portal.closeRequest(id), onSuccess: refresh });
}

/** The club's roles, for choosing who answers general messages. */
export function useClubRoles() {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "roles", "all"],
    retry: false,
    queryFn: ({ signal }) => fetchAllPages((page) => api.core.listRoles({ ...page, limit: 200 }), { signal }),
  });
}
